import {
  Group, Mesh, BoxGeometry, CylinderGeometry,
  MeshLambertMaterial, MeshPhongMaterial,
} from 'three';
import { quad, buildGeo } from './ModelUtils';

/**
 * SF-0 ファントムブレード — ステルス戦闘機モデル
 *
 * 6角形断面 × 10ステーション押し出し方式。
 */
export function buildStealthModel(): Group {
  const root = new Group();

  const matBody   = new MeshPhongMaterial({ color: 0x6a6a78, shininess: 30, flatShading: true });
  const matDark   = new MeshPhongMaterial({ color: 0x3e3e4a, shininess: 20, flatShading: true });
  const matUnder  = new MeshPhongMaterial({ color: 0x505060, shininess: 15, flatShading: true });
  const matCanopy = new MeshPhongMaterial({
    color: 0x182838, emissive: 0x000c18, shininess: 250,
    transparent: true, opacity: 0.78,
  });
  const matNozzle = new MeshPhongMaterial({
    color: 0x252528, emissive: 0x100600, emissiveIntensity: 0.3, shininess: 100,
  });
  const matIntake = new MeshLambertMaterial({ color: 0x0e0e12 });

  // ════════════════════════════════════════════════
  // 胴体（テーパー六角形断面を押し出し）
  // ════════════════════════════════════════════════
  const stations: { z: number; tw: number; ty: number; cw: number; cy: number; bw: number; by: number; boy: number }[] = [
    { z: -10.5, tw: 0.0,  ty: 0.10, cw: 0.08, cy: 0.0,   bw: 0.05, by: -0.05, boy: -0.08 },
    { z: -8.5,  tw: 0.25, ty: 0.22, cw: 0.55, cy: 0.05,  bw: 0.40, by: -0.10, boy: -0.15 },
    { z: -6.0,  tw: 0.50, ty: 0.35, cw: 1.10, cy: 0.10,  bw: 0.80, by: -0.12, boy: -0.18 },
    { z: -4.0,  tw: 0.65, ty: 0.55, cw: 1.45, cy: 0.12,  bw: 1.10, by: -0.12, boy: -0.20 },
    { z: -1.5,  tw: 0.75, ty: 0.50, cw: 1.70, cy: 0.10,  bw: 1.50, by: -0.12, boy: -0.22 },
    { z:  0.0,  tw: 0.80, ty: 0.42, cw: 1.80, cy: 0.08,  bw: 1.70, by: -0.12, boy: -0.22 },
    { z:  2.5,  tw: 0.75, ty: 0.40, cw: 1.70, cy: 0.06,  bw: 1.60, by: -0.12, boy: -0.22 },
    { z:  5.0,  tw: 0.60, ty: 0.38, cw: 1.40, cy: 0.06,  bw: 1.30, by: -0.12, boy: -0.22 },
    { z:  7.0,  tw: 0.45, ty: 0.35, cw: 1.15, cy: 0.04,  bw: 1.10, by: -0.10, boy: -0.20 },
    { z:  8.2,  tw: 0.30, ty: 0.30, cw: 0.95, cy: 0.02,  bw: 0.90, by: -0.08, boy: -0.18 },
  ];

  function secVerts(s: typeof stations[0]): number[][] {
    return [
      [0,      s.ty,  s.z],
      [ s.cw,  s.cy,  s.z],
      [ s.bw,  s.by,  s.z],
      [0,      s.boy, s.z],
      [-s.bw,  s.by,  s.z],
      [-s.cw,  s.cy,  s.z],
    ];
  }

  // 胴体上面
  {
    const v: number[] = [], n: number[] = [];
    for (let i = 0; i < stations.length - 1; i++) {
      const a = secVerts(stations[i]);
      const b = secVerts(stations[i + 1]);
      for (let j = 0; j < 6; j++) {
        const j2 = (j + 1) % 6;
        quad(v, n,
          a[j][0], a[j][1], a[j][2],
          b[j][0], b[j][1], b[j][2],
          b[j2][0], b[j2][1], b[j2][2],
          a[j2][0], a[j2][1], a[j2][2],
        );
      }
    }
    root.add(new Mesh(buildGeo(v, n), matBody));
  }

  // 胴体下面
  {
    const v: number[] = [], n: number[] = [];
    for (let i = 0; i < stations.length - 1; i++) {
      const a = secVerts(stations[i]);
      const b = secVerts(stations[i + 1]);
      quad(v, n,
        a[2][0], a[2][1], a[2][2],
        b[2][0], b[2][1], b[2][2],
        b[3][0], b[3][1], b[3][2],
        a[3][0], a[3][1], a[3][2],
      );
      quad(v, n,
        a[3][0], a[3][1], a[3][2],
        b[3][0], b[3][1], b[3][2],
        b[4][0], b[4][1], b[4][2],
        a[4][0], a[4][1], a[4][2],
      );
    }
    root.add(new Mesh(buildGeo(v, n), matUnder));
  }

  // ════════════════════════════════════════════════
  // キャノピー
  // ════════════════════════════════════════════════
  {
    const v: number[] = [], n: number[] = [];
    const cpSections: { z: number; w: number; h: number }[] = [
      { z: -4.8, w: 0.0,  h: 0.0 },
      { z: -4.0, w: 0.35, h: 0.22 },
      { z: -3.0, w: 0.50, h: 0.38 },
      { z: -2.0, w: 0.48, h: 0.35 },
      { z: -1.3, w: 0.30, h: 0.18 },
    ];
    for (let i = 0; i < cpSections.length - 1; i++) {
      const a = cpSections[i], b = cpSections[i + 1];
      const ay = 0.55 + a.h, by = 0.55 + b.h;
      const aBase = 0.52, bBase = 0.52;
      quad(v, n, -a.w, ay, a.z, -b.w, by, b.z, b.w, by, b.z, a.w, ay, a.z);
      quad(v, n, -a.w, aBase, a.z, -b.w, bBase, b.z, -b.w, by, b.z, -a.w, ay, a.z);
      quad(v, n, a.w, ay, a.z, b.w, by, b.z, b.w, bBase, b.z, a.w, aBase, a.z);
    }
    root.add(new Mesh(buildGeo(v, n), matCanopy));

    const frameMat = matDark;
    const frameF = new Mesh(new BoxGeometry(1.0, 0.08, 0.12), frameMat);
    frameF.position.set(0, 0.56, -4.5);
    frameF.rotation.x = -0.35;
    root.add(frameF);
    const frameR = new Mesh(new BoxGeometry(0.7, 0.06, 0.1), frameMat);
    frameR.position.set(0, 0.60, -1.35);
    root.add(frameR);
  }

  // ════════════════════════════════════════════════
  // 主翼
  // ════════════════════════════════════════════════
  for (const sx of [-1, 1]) {
    const v: number[] = [], n: number[] = [];
    const rootLE_x = sx * 1.5,  rootLE_z = -1.5;
    const rootTE_x = sx * 1.3,  rootTE_z = 4.5;
    const tipLE_x  = sx * 7.8,  tipLE_z  = 1.5;
    const tipTE_x  = sx * 5.5,  tipTE_z  = 4.0;
    const rootY = -0.10, tipY = -0.03;
    const thick = 0.15, tipThick = 0.06;

    quad(v, n, rootLE_x, rootY+thick, rootLE_z, tipLE_x, tipY+tipThick, tipLE_z, tipTE_x, tipY+tipThick, tipTE_z, rootTE_x, rootY+thick, rootTE_z);
    quad(v, n, rootLE_x, rootY-thick, rootLE_z, rootTE_x, rootY-thick, rootTE_z, tipTE_x, tipY-tipThick, tipTE_z, tipLE_x, tipY-tipThick, tipLE_z);
    quad(v, n, rootLE_x, rootY-thick, rootLE_z, tipLE_x, tipY-tipThick, tipLE_z, tipLE_x, tipY+tipThick, tipLE_z, rootLE_x, rootY+thick, rootLE_z);
    quad(v, n, rootTE_x, rootY+thick, rootTE_z, tipTE_x, tipY+tipThick, tipTE_z, tipTE_x, tipY-tipThick, tipTE_z, rootTE_x, rootY-thick, rootTE_z);
    quad(v, n, tipLE_x, tipY+tipThick, tipLE_z, tipLE_x, tipY-tipThick, tipLE_z, tipTE_x, tipY-tipThick, tipTE_z, tipTE_x, tipY+tipThick, tipTE_z);

    root.add(new Mesh(buildGeo(v, n), matBody));
  }

  // ════════════════════════════════════════════════
  // 水平尾翼
  // ════════════════════════════════════════════════
  for (const sx of [-1, 1]) {
    const v: number[] = [], n: number[] = [];
    const rle_x = sx * 0.8, rle_z = 5.0;
    const rte_x = sx * 1.0, rte_z = 7.5;
    const tle_x = sx * 3.2, tle_z = 6.0;
    const tte_x = sx * 2.5, tte_z = 7.8;
    const y = -0.05, t = 0.07;

    quad(v, n, rle_x, y+t, rle_z, tle_x, y+t, tle_z, tte_x, y+t, tte_z, rte_x, y+t, rte_z);
    quad(v, n, rle_x, y-t, rle_z, rte_x, y-t, rte_z, tte_x, y-t, tte_z, tle_x, y-t, tle_z);
    quad(v, n, rle_x, y-t, rle_z, tle_x, y-t, tle_z, tle_x, y+t, tle_z, rle_x, y+t, rle_z);
    quad(v, n, rte_x, y+t, rte_z, tte_x, y+t, tte_z, tte_x, y-t, tte_z, rte_x, y-t, rte_z);

    root.add(new Mesh(buildGeo(v, n), matDark));
  }

  // ════════════════════════════════════════════════
  // 垂直尾翼（外傾±25°）
  // ════════════════════════════════════════════════
  for (const sx of [-1, 1]) {
    const v: number[] = [], n: number[] = [];
    const rb_x = sx * 0.7, rb_z = 4.2, rb_y = 0.35;
    const rt_x = sx * 0.9, rt_z = 7.2, rt_y = 0.35;
    const cantOffset = sx * 0.9;
    const tb_x = rb_x + cantOffset, tb_z = 5.0, tb_y = 2.4;
    const tt_x = rt_x + cantOffset, tt_z = 7.0, tt_y = 1.8;
    const t = 0.10;

    quad(v, n, rb_x+t*sx, rb_y, rb_z, tb_x+t*sx, tb_y, tb_z, tt_x+t*sx, tt_y, tt_z, rt_x+t*sx, rt_y, rt_z);
    quad(v, n, rb_x-t*sx, rb_y, rb_z, rt_x-t*sx, rt_y, rt_z, tt_x-t*sx, tt_y, tt_z, tb_x-t*sx, tb_y, tb_z);
    quad(v, n, rb_x-t*sx, rb_y, rb_z, tb_x-t*sx, tb_y, tb_z, tb_x+t*sx, tb_y, tb_z, rb_x+t*sx, rb_y, rb_z);
    quad(v, n, rt_x+t*sx, rt_y, rt_z, tt_x+t*sx, tt_y, tt_z, tt_x-t*sx, tt_y, tt_z, rt_x-t*sx, rt_y, rt_z);
    quad(v, n, tb_x-t*sx, tb_y, tb_z, tt_x-t*sx, tt_y, tt_z, tt_x+t*sx, tt_y, tt_z, tb_x+t*sx, tb_y, tb_z);

    root.add(new Mesh(buildGeo(v, n), matDark));
  }

  // ════════════════════════════════════════════════
  // インテーク
  // ════════════════════════════════════════════════
  for (const sx of [-1, 1]) {
    const intake = new Mesh(new BoxGeometry(0.9, 0.55, 2.5), matIntake);
    intake.position.set(sx * 1.55, -0.15, 0.3);
    root.add(intake);
  }

  // ════════════════════════════════════════════════
  // エンジンノズル
  // ════════════════════════════════════════════════
  for (const sx of [-0.88, 0.88]) {
    const outer = new Mesh(new CylinderGeometry(0.48, 0.55, 1.8, 12), matDark);
    outer.rotation.x = Math.PI / 2;
    outer.position.set(sx, -0.06, 7.3);
    root.add(outer);

    const inner = new Mesh(new CylinderGeometry(0.35, 0.35, 0.3, 12), matNozzle);
    inner.rotation.x = Math.PI / 2;
    inner.position.set(sx, -0.06, 8.2);
    root.add(inner);
  }

  const nozzleFair = new Mesh(new BoxGeometry(1.8, 0.65, 2.5), matDark);
  nozzleFair.position.set(0, -0.06, 7.0);
  root.add(nozzleFair);

  // ════════════════════════════════════════════════
  // ピトー管
  // ════════════════════════════════════════════════
  const pitot = new Mesh(new CylinderGeometry(0.03, 0.03, 1.5, 5), matDark);
  pitot.rotation.x = Math.PI / 2;
  pitot.position.set(-0.3, 0.18, -10.2);
  root.add(pitot);

  return root;
}
